import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ItemStatus } from "@focus/focusUI";
import { ofType } from "redux-observable";
import { mergeMap, map, catchError, of } from "rxjs";
import { createAsyncReducers } from "./helpers";
import { type CatalogueObject, type PageResponse } from "../types/catalog";

const baseURL = "/api/v1";

export type CatalogueRequestKey =
  | "getCatalogueObjects"
  | "getCatalogueObjectInfo";

type RequestState = {
  status: ItemStatus;
  error: string | null;
};

type RequestsMap = Record<CatalogueRequestKey, RequestState>;

type CataloguePayload = {
  catalogue: PageResponse<CatalogueObject>;
};

type CatalogueInfoPayload = {
  object: CatalogueObject;
};

type CatalogueState = CataloguePayload &
  CatalogueInfoPayload & {
    requests: RequestsMap;
  };

const initialState: CatalogueState = {
  catalogue: undefined,
  object: undefined,
  requests: {
    getCatalogueObjects: { status: ItemStatus.READY, error: null },
    getCatalogueObjectInfo: { status: ItemStatus.READY, error: null },
  },
};

export const catalogueMessages = {
  loadError: "Could not load contacts.",
  genericError: "Something went wrong with the contact.",
};

const getCatalogueObjectsAsync = createAsyncReducers<
  CatalogueState,
  CatalogueRequestKey,
  PageResponse<CatalogueObject>,
  { page?: number; pageSize?: number; filter?: string; sort?: string }
>("getCatalogueObjects", (state, action) => {
  state.catalogue = action.payload;
});

const getCatalogueObjectInfoAsync = createAsyncReducers<
  CatalogueState,
  CatalogueRequestKey,
  CatalogueObject,
  { id: string }
>("getCatalogueObjectInfo", (state, action) => {
  state.object = action.payload;
});

const catalogueSlice = createSlice({
  name: "catalogue-objects",
  initialState,
  reducers: {
    getCatalogueObjectsRequest: getCatalogueObjectsAsync.request,
    getCatalogueObjectsSuccess: getCatalogueObjectsAsync.success,
    getCatalogueObjectsFailure: getCatalogueObjectsAsync.failure,
    getCatalogueObjectInfoRequest: getCatalogueObjectInfoAsync.request,
    getCatalogueObjectInfoSuccess: getCatalogueObjectInfoAsync.success,
    getCatalogueObjectInfoFailure: getCatalogueObjectInfoAsync.failure,
  },
});

export const {
  getCatalogueObjectsRequest,
  getCatalogueObjectsSuccess,
  getCatalogueObjectsFailure,
  getCatalogueObjectInfoRequest,
  getCatalogueObjectInfoSuccess,
  getCatalogueObjectInfoFailure,
} = catalogueSlice.actions;

const getCatalogueObjectsEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getCatalogueObjectsRequest.type),
    mergeMap(
      (
        action: PayloadAction<
          | {
              page?: number;
              pageSize?: number;
              filter?: string;
              sort?: string;
            }
          | undefined
        >,
      ) => {
        const params = new URLSearchParams();
        const page = action.payload?.page;
        const pageSize = action.payload?.pageSize;
        const filter = action.payload?.filter;
        const sort = action.payload?.sort;

        if (typeof page === "number") {
          params.set("page", String(page));
        }

        if (typeof pageSize === "number") {
          params.set("pageSize", String(pageSize));
        }

        if (typeof filter === "string" && filter.trim()) {
          params.set("filter", filter);
        }

        if (typeof sort === "string" && sort.trim()) {
          params.set("sort", sort);
        }

        const query = params.toString();
        const url = `${baseURL}/catalogue${query ? `?${query}` : ""}`;
        return ajax.getJSON(url).pipe(
          map((page: PageResponse<CatalogueObject>) =>
            getCatalogueObjectsSuccess(page),
          ),
          catchError((error) => {
            return of(
              getCatalogueObjectsFailure(
                error.message || catalogueMessages.loadError,
              ),
            );
          }),
        );
      },
    ),
  );
};

const getCatalogueObjectInfoEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getCatalogueObjectInfoRequest.type),
    mergeMap((action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const url = `${baseURL}/catalogue/${id}`;
      return ajax.getJSON(url).pipe(
        map((object: CatalogueObject) => getCatalogueObjectInfoSuccess(object)),
        catchError((error) => {
          return of(
            getCatalogueObjectInfoFailure(
              error.message || "Could not load contacts.",
            ),
          );
        }),
      );
    }),
  );
};

export default {
  actions: catalogueSlice.actions,
  reducer: catalogueSlice.reducer,
  middleware: [getCatalogueObjectsEpic, getCatalogueObjectInfoEpic],
  selectors: catalogueSlice.selectors,
};
